import { UserArgType, } from "@/UI/Forms/FormFull/FormFullInterfaces";
import { TestCommand } from "@/Testing/TestInterfaces";
import { slugify } from "@/Core/Utils/StringUtils";
import { isLocalHost } from "@/Core/GlobalVars";
import { setFocus, isElementValueCorrect } from "./TourUtils";
/**
 * Builds an HTML ordered list of available options for a Select user argument.
 * Returns an empty string if the argument is not a Select type or has no options.
 *
 * @param {UserArg | undefined} userArg The user argument to extract options from.
 * @returns {string} An HTML string with the options list, or empty string.
 */
function buildSelectOptionsHtml(userArg) {
    if (!userArg || userArg.type !== UserArgType.Select) {
        return "";
    }
    const options = userArg.options;
    if (!options || options.length === 0) {
        return "";
    }
    const listItems = options.map((opt) => {
        const label = typeof opt === "string" ? opt : opt.description;
        return `<li>${label}</li>`;
    }).join("");
    return `<div class="mt-3 mb-0"><strong>Available options:</strong><ol class="mb-0">${listItems}</ol></div>`;
}
/**
 * Builds an HTML string for the description and warning of a user argument.
 * Returns an empty string if the argument has neither.
 *
 * @param {UserArg | undefined} userArg The user argument.
 * @param {any} [currentValue] The current value, used for warningFunc evaluation.
 * @param {boolean} [skipWarning] If true, skip the warningFunc call. Used
 *   when the command targets a sub-field of a composite arg (e.g., a single
 *   axis of a SelectRegion), where the scalar value doesn't satisfy the
 *   warningFunc's expected shape.
 * @returns {string} An HTML string with description and/or warning.
 */
function buildArgDetailsHtml(userArg, currentValue, skipWarning = false) {
    if (!userArg) {
        return "";
    }
    const parts = [];
    if (userArg.description) {
        parts.push(`<div class="mt-3 mb-0"><strong>Brief description:</strong> <em>${userArg.description}</em></div>`);
    }
    if (userArg.warningFunc && !skipWarning) {
        try {
            const warning = userArg.warningFunc(currentValue);
            if (warning) {
                parts.push(`<br><br><strong class="text-danger">Warning:</strong> ${warning}`);
            }
        }
        catch (err) {
            // warningFunc may expect a specific shape (e.g., an object
            // with .center and .dimensions) that doesn't match the scalar
            // currentValue when this command targets a sub-field. Silently
            // skip the warning in that case rather than crashing the tour.
            if (isLocalHost) {
                console.log(`[Tour Debug] buildArgDetailsHtml: warningFunc threw for arg "${userArg.id}", skipping. Error: ${err.message}`);
            }
        }
    }
    return parts.join("");
}
/**
 * Finds the user argument corresponding to a selector and refines the selector to target the specific input element.
 * Also reports whether the command targets a sub-field of a composite arg
 * (e.g., one axis of a SelectRegion), which is useful for deciding whether
 * to invoke the arg's warningFunc (which expects the whole composite value).
 *
 * @param {ITestCommand} command The test command containing the base selector.
 * @param {PluginParentClass} plugin The plugin instance.
 * @returns {{ userArg: UserArg | undefined, specificSelector: string, isCompositeSubField: boolean }} The found user argument, the refined selector, and whether this is a sub-field of a composite arg.
 */
export function findUserArgAndRefineSelector(command, plugin) {
    let specificSelector = command.selector;
    let foundUserArg;
    let isCompositeSubField = false;
    if (command.selector) {
        const selectorParts = command.selector.split(" ");
        const lastSelectorPart = selectorParts[selectorParts.length - 1];
        const modalSelectorPart = selectorParts.slice(0, -1).join(" ");
        const selectorStr = lastSelectorPart.replace(/^#/, "");
        const suffix = `-${plugin.pluginId}-item`;
        if (selectorStr.endsWith(suffix)) {
            const argId = selectorStr.slice(0, -suffix.length);
            const allArgs = plugin.userArgsMixin.getUserArgsFlat();
            foundUserArg = allArgs.find((arg) => arg.id === argId);
            if (foundUserArg) {
                let tagName = "";
                switch (foundUserArg.type) {
                    case UserArgType.Text:
                    case UserArgType.Number:
                    case UserArgType.Color:
                    case UserArgType.Range:
                    case UserArgType.ListSelect:
                        tagName = "input";
                        break;
                    case UserArgType.TextArea:
                        tagName = "textarea";
                        break;
                    case UserArgType.Select:
                    case UserArgType.SelectMolecule:
                    case UserArgType.SelectRegion:
                        tagName = "select";
                        break;
                }
                if (tagName) {
                    specificSelector = `${modalSelectorPart} ${tagName}${lastSelectorPart}`;
                }
            }
            else {
                // No direct id match. Handle composite ids like
                // "x-center-region", "y-dimens-region", etc. These target
                // sub-fields of a larger compound arg (e.g., a SelectRegion
                // whose id is just "region"). We look for a real arg whose
                // id matches a *suffix* of the composite id, which lets us
                // correctly attribute the command to its parent arg so UI
                // ordering is preserved during tours.
                const axisMatch = argId.match(/^([xyz])-/);
                if (axisMatch) {
                    specificSelector = `${modalSelectorPart} input${lastSelectorPart}`;
                    // Try progressively shorter suffixes. For
                    // "x-center-region", try "center-region", then "region".
                    // The longest matching real arg id wins so that a
                    // hypothetical "center-region" arg would be preferred
                    // over a bare "region" arg if both existed.
                    const withoutAxis = argId.substring(2);
                    const parts = withoutAxis.split("-");
                    for (let startIdx = 0; startIdx < parts.length; startIdx++) {
                        const candidate = parts.slice(startIdx).join("-");
                        const match = allArgs.find((arg) => arg.id === candidate);
                        if (match) {
                            foundUserArg = match;
                            isCompositeSubField = true;
                            break;
                        }
                    }
                }
            }
        }
    }
    return { userArg: foundUserArg, specificSelector, isCompositeSubField };
}
/**
 * Builds a human-readable label for a form field.
 *
 * @param {UserArg | undefined} userArg The user argument object for the field.
 * @param {string} commandSelector The original selector from the command.
 * @param {string} pluginId The ID of the current plugin.
 * @returns {string} The constructed field label.
 */
function buildFieldLabel(userArg, commandSelector, pluginId) {
    if (userArg) {
        // Prioritize Vector3D check for specific axis labeling
        if (userArg.type === UserArgType.Vector3D) {
            const argId = commandSelector
                .replace(/^#/, "")
                .replace(`-${pluginId}-item`, "");
            const axisMatch = argId.match(/^([xyz])-/);
            if (axisMatch) {
                const axis = axisMatch[1].toUpperCase();
                const groupLabel = userArg.label && userArg.label.trim() !== ""
                    ? ` of ${userArg.label.trim()}`
                    : "";
                return `the ${axis} value${groupLabel}`;
            }
        }
        if (userArg.label && userArg.label.trim() !== "") {
            return `${userArg.label.trim()}`;
        }
        if (userArg.placeHolder) {
            const placeholder = userArg.placeHolder;
            const nameFromPlaceholder = placeholder
                .split("(")[0]
                .trim()
                .replace(/\.\.\.$/, "")
                .trim();
            if (nameFromPlaceholder) {
                return `${nameFromPlaceholder}`;
            }
        }
    }
    return "this input";
}
/**
 * Creates a driver.js step for a click command.
 *
 * @param {ITestCommand} command The click command.
 * @param {PluginParentClass} plugin The plugin instance.
 * @param {ITourContext} context The tour context.
 * @returns {any} A driver.js step object.
 */
export function createClickStep(command, plugin, context) {
    const selector = command.selector || "";
    const isShiftClick = command.data === true;
    const popover = {
        title: plugin.title,
        description: "Please click here to continue.",
    };
    const selectMoleculeMatch = selector.match(/#navigator div\[data-label="([^"]+)"\]/);
    if (selectMoleculeMatch) {
        const moleculeName = selectMoleculeMatch[1];
        const action = command.tourMessage || "select it";
        if (isShiftClick) {
            popover.description = `Hold <b>Shift</b> and click on this "${moleculeName}" item in the Navigator panel to add it to the selection.`;
        }
        else {
            popover.description = `Please click on this "${moleculeName}" item in the Navigator panel to ${action}.`;
        }
    }
    // If a custom tour message is provided and no molecule match was found,
    // use it as the full description override.
    if (!selectMoleculeMatch && command.tourMessage) {
        popover.description = command.tourMessage;
    }
    // If the click targets a user arg (e.g., a checkbox), look up its
    // label so the popover says something meaningful instead of the
    // generic "Please click here to continue."
    if (!selectMoleculeMatch && !command.tourMessage) {
        const { userArg } = findUserArgAndRefineSelector(command, plugin);
        if (userArg) {
            const label = userArg.label || userArg.id;
            if (userArg.type === UserArgType.Checkbox) {
                popover.description = `Click to toggle <b>${label}</b>.`;
                if (userArg.description) {
                    popover.description += ` <div class="mt-3 mb-0"><strong>Brief description:</strong> <em>${userArg.description}</em></div>`;
                }
            }
            else {
                popover.description = `Please click <b>${label}</b> to continue.`;
            }
        }
    }
    // Detect if this is a menu item selector (inside dropdown menus or nav items)
    // These are typically positioned near the left edge and benefit from right-side popovers
    // Patterns to detect:
    // - .dropdown-menu, .dropdown-item, .nav-link (class-based)
    // - .navbar #menu1-xxx (top-level navbar menus)
    // - Selectors containing #menu followed by digits (menu item IDs)
    const isMenuItem = selector.includes(".dropdown-menu") ||
        selector.includes(".dropdown-item") ||
        selector.includes(".nav-link") ||
        selector.includes(".navbar") ||
        /#menu\d/.test(selector);
    if (isLocalHost) {
        console.log(`[Tour Debug] createClickStep: selector="${selector}", isMenuItem=${isMenuItem}`);
    }
    // For menu items, we want to highlight the parent <li> for better popover positioning,
    // but still attach the click listener to the actual menu item element.
    // We'll find the parent li dynamically in onHighlighted.
    const step = {
        element: selector,
        popover: popover,
        isClickStep: true,
        isMenuItem: isMenuItem,
        actualClickSelector: selector, // Store original selector for click handling
    };
    // For menu items, force the popover to appear on the right side
    // This prevents the popover from obscuring the menu item when it's near the left edge
    // Note: side/align must be at step level, not inside popover
    if (isMenuItem) {
        step.popover.side = "right";
        step.popover.align = "start";
    }
    step.onHighlighted = (element) => {
        if (isLocalHost) {
            console.log(`[Tour Debug] createClickStep onHighlighted called for selector "${selector}", element:`, element);
        }
        if (!element) {
            console.error(`TourManager: Element not found for selector "${selector}". Skipping step.`);
            if (context.manager.driver) {
                context.manager.driver.moveNext();
            }
            return;
        }
        // For menu items, try to re-highlight using the parent <li> element
        // This provides better popover positioning for items near screen edges
        if (isMenuItem && context.manager.driver) {
            const parentLi = element.closest("li.nav-item, li.dropdown-item, li");
            if (parentLi && parentLi !== element) {
                if (isLocalHost) {
                    console.log(`[Tour Debug] Found parent <li> for menu item, re-highlighting:`, parentLi);
                }
                // Get current step and update its element to the parent
                const activeStep = context.manager.driver.getActiveStep();
                if (activeStep) {
                    // Temporarily add a unique attribute to target the parent
                    parentLi.setAttribute("data-tour-menu-highlight", "true");
                    activeStep.element = "[data-tour-menu-highlight='true']";
                    // Refresh to re-highlight with the new element
                    // The popover will be shown by handlePopoverRender after this refresh
                    context.manager.driver.refresh();
                    // Manually show the popover after refresh since onPopoverRender may not be called again
                    requestAnimationFrame(() => {
                        const popoverEl = document.querySelector(".driver-popover");
                        if (popoverEl) {
                            popoverEl.classList.remove("tour-menu-item-hidden");
                            if (isLocalHost) {
                                console.log(`[Tour Debug] Manually showing popover after refresh`);
                            }
                        }
                    });
                    // Clean up the attribute after a short delay
                    setTimeout(() => {
                        parentLi.removeAttribute("data-tour-menu-highlight");
                    }, 100);
                }
            }
        }
        setTimeout(() => {
            if (context.manager.driver && context.manager.driver.refresh) {
                if (isLocalHost) {
                    console.log(`[Tour Debug] Calling driver.refresh() for selector "${selector}"`);
                }
                context.manager.driver.refresh();
            }
        }, 1000);
        setFocus(element);
        // For menu items, we need to attach the click listener to the actual clickable element,
        // not the parent <li> that we're highlighting
        const clickTarget = isMenuItem
            ? document.querySelector(selector) || element
            : element;
        const oneTimeClickListener = () => {
            clickTarget.removeEventListener("click", oneTimeClickListener);
            if (!context.manager.driver) {
                return;
            }
            const isLastStep = !context.manager.driver.hasNextStep();
            let nextStepHasNoElement = false;
            if (!isLastStep) {
                const activeIndex = context.manager.driver.getActiveIndex();
                const nextStep = context.manager.tourSteps[activeIndex + 1];
                nextStepHasNoElement = nextStep && !nextStep.element;
            }
            const needsDelayForNativeHandler = isLastStep || nextStepHasNoElement;
            if (needsDelayForNativeHandler) {
                requestAnimationFrame(() => {
                    setTimeout(() => {
                        if (context.manager.driver) {
                            if (isLastStep) {
                                context.markCompleted();
                                context.manager.driver.destroy();
                            }
                            else {
                                context.manager.driver.moveNext();
                            }
                        }
                    }, 150);
                });
            }
            else {
                setTimeout(() => {
                    if (context.manager.driver) {
                        context.manager.driver.moveNext();
                    }
                }, 0);
            }
        };
        clickTarget.addEventListener("click", oneTimeClickListener);
    };
    return step;
}
/**
 * Creates a driver.js step for a text input or file upload command.
 *
 * @param {ITestCommand} command The input command.
 * @param {PluginParentClass} plugin The plugin instance.
 * @param {ITourContext} context The tour context.
 * @returns {any} A driver.js step object.
 */
export function createInputStep(command, plugin, context) {
    const { userArg, specificSelector, isCompositeSubField } = findUserArgAndRefineSelector(command, plugin);
    const fieldLabel = buildFieldLabel(userArg, command.selector, plugin.pluginId);
    const popover = {
        title: plugin.title,
        description: "",
    };
    if (command.cmd === TestCommand.Upload) {
        popover.description = `Please click to upload a file for ${fieldLabel}. The tour will advance automatically once the file is loaded and processed.`;
        return {
            element: specificSelector,
            popover,
            isUploadStep: true,
            onHighlighted: (element) => {
                if (!element) {
                    console.error(`TourManager: Element not found for selector "${specificSelector}". Skipping step.`);
                    context.manager.driver.moveNext();
                    return;
                }
                // Poll for file upload completion by watching the input
                // element and downstream indicators (e.g., action button
                // becoming enabled, or navigator content updating).
                const pollForUploadCompletion = () => {
                    const maxWaitMs = 60000;
                    const pollIntervalMs = 500;
                    const startTime = Date.now();
                    const actionBtnSelector = `#modal-${plugin.pluginId} .action-btn`;
                    const checkReady = () => {
                        if (!context.manager.driver) {
                            return;
                        }
                        // Check if the action button is now enabled,
                        // indicating files have been loaded and the plugin
                        // is ready to proceed.
                        const actionBtn = document.querySelector(actionBtnSelector);
                        const actionBtnEnabled = actionBtn && !actionBtn.disabled;
                        // Also check the file input itself for files.
                        const fileInput = element.tagName.toLowerCase() === 'input'
                            ? element
                            : element.querySelector('input[type="file"]');
                        const hasFiles = fileInput && fileInput.files && fileInput.files.length > 0;
                        if (hasFiles && actionBtnEnabled) {
                            if (isLocalHost) {
                                console.log(`[Tour Debug] Upload complete: files detected and action button enabled.`);
                            }
                            context.manager.driver.moveNext();
                            return;
                        }
                        if (Date.now() - startTime > maxWaitMs) {
                            console.warn(`TourManager: Upload wait timed out after ${maxWaitMs}ms. Advancing tour.`);
                            context.manager.driver.moveNext();
                            return;
                        }
                        setTimeout(checkReady, pollIntervalMs);
                    };
                    checkReady();
                };
                pollForUploadCompletion();
            },
        };
    }
    let valueForDisplay = command.data;
    let actionVerb = "enter";
    if (userArg?.type === UserArgType.Select) {
        actionVerb = "select";
        const options = userArg.options;
        const foundOption = options.find((opt) => {
            const optionVal = typeof opt === "string" ? slugify(opt) : opt.val;
            return optionVal == command.data;
        });
        if (foundOption) {
            valueForDisplay =
                typeof foundOption === "string"
                    ? foundOption
                    : foundOption.description;
        }
    }
    const descriptionParts = [
        `For ${fieldLabel}, ${actionVerb} "<span class="value-to-display">${valueForDisplay}</span>".`,
    ];
    descriptionParts.push(buildSelectOptionsHtml(userArg));
    descriptionParts.push(buildArgDetailsHtml(userArg, command.data, isCompositeSubField));
    popover.description = descriptionParts.join("");
    return {
        element: specificSelector,
        popover,
        expectedValue: command.data, // Pass expected value to step for runtime checking
        onHighlightStarted: (element) => {
            if (!element) {
                console.error(`TourManager: Element not found for selector "${specificSelector}". Skipping step.`);
                context.manager.driver.moveNext();
                return;
            }
            const checkAndAdvance = () => {
                if (isElementValueCorrect(element, command.data)) {
                    element.removeEventListener("input", checkAndAdvance);
                    element.removeEventListener("change", checkAndAdvance);
                    context.manager.driver.moveNext();
                }
            };
            element.addEventListener("input", checkAndAdvance);
            element.addEventListener("change", checkAndAdvance);
        },
        onHighlighted: (element) => {
            setFocus(element);
        },
    };
}
/**
 * Creates a driver.js step for a wait command. During a tour, the step
 * polls the wait condition and auto-advances when satisfied.
 *
 * @param {ITestCommand} command The wait command.
 * @param {PluginParentClass} plugin The plugin instance.
 * @param {ITourContext} context The tour context.
 * @returns {any} A driver.js step object.
 */
export function createWaitStep(command, plugin, context) {
    return {
        popover: {
            title: plugin.title,
            description: "Waiting for the application to update...",
        },
        isWaitStep: true,
        waitCondition: () => {
            const el = document.querySelector(command.selector);
            if (!el)
                return false;
            const regex = new RegExp(command.data);
            // Check both textContent (for plain text matches) and innerHTML
            // (for markup-based matches like SVG icon attributes). textContent
            // strips tags, so regexes targeting HTML/SVG structure would fail
            // if only textContent were checked.
            const textContent = el.textContent || "";
            if (regex.test(textContent))
                return true;
            const htmlContent = el.innerHTML || "";
            return regex.test(htmlContent);
        },
        /**
         * Polls the wait condition and auto-advances the tour when satisfied.
         * This fires when the step has an element to highlight. For steps
         * without an element, polling is started in TourManager.moveToNextStepWithRetry.
         *
         * @param {HTMLElement} _element The highlighted element (unused for wait steps).
         */
        onHighlighted: (_element) => {
            void _element;
            const pollInterval = setInterval(() => {
                // Stop polling if the tour was destroyed
                if (!context.manager.driver) {
                    clearInterval(pollInterval);
                    return;
                }
                const activeStep = context.manager.driver.getActiveStep();
                if (!activeStep || !activeStep.waitCondition) {
                    clearInterval(pollInterval);
                    return;
                }
                if (activeStep.waitCondition()) {
                    clearInterval(pollInterval);
                    context.manager.driver.moveNext();
                }
            }, 500);
        },
    };
}
/**
 * Creates a driver.js step for a tour note command.
 *
 * @param {ITestCommand} command The tour note command.
 * @param {PluginParentClass} plugin The plugin instance.
 * @returns {any} A driver.js step object.
 */
export function createNoteStep(command, plugin) {
    return {
        element: command.selector,
        popover: {
            title: plugin.title,
            description: command.data,
        },
        isNoteStep: true,
        onHighlighted: (element) => {
            setFocus(element);
        },
    };
}
/**
 * Creates a default driver.js step for a user argument that isn't modified in the test.
 * Since the test doesn't change this argument, the tour presents it as an
 * informational step, instructing the user to leave the current value as is.
 * The input element is temporarily disabled to prevent accidental changes that
 * could break the tour flow.
 *
 * @param {UserArg} arg The user argument.
 * @param {PluginParentClass} plugin The plugin instance.
 * @returns {any} A driver.js step object.
 */
export function createDefaultArgStep(arg, plugin) {
    let selector = `#${arg.id}-${plugin.pluginId}-item`;
    let mainText = "";
    if (arg.type === UserArgType.Vector3D) {
        selector = `#x-${arg.id}-${plugin.pluginId}-item`;
    }
    else if (arg.type === UserArgType.MoleculeInputParams) {
        // Define possible selector variations to ensure the element is found
        const baseId = `${arg.id}-${plugin.pluginId}-item`;
        const baseIdWithModal = `${arg.id}-modal-${plugin.pluginId}-item`;
        // 1. Expected ID (standard form)
        // 2. ID with 'modal-' prefix (if FormFull context differs)
        // 3. Fallback: Select element inside the container (if prop passing fails)
        selector = `#${baseId}-molecule-selection, #${baseIdWithModal}-molecule-selection, #${baseId} select`;
        let targetDescription = "molecules";
        if (arg.label && arg.label.trim() !== "") {
            targetDescription = arg.label.toLowerCase();
            const suffixesToRemove = [
                " to consider",
                " to align",
                " to use as queries",
            ];
            for (const suffix of suffixesToRemove) {
                if (targetDescription.endsWith(suffix)) {
                    targetDescription = targetDescription.substring(0, targetDescription.length - suffix.length);
                    break;
                }
            }
        }
        const targetDescParts = targetDescription.split(" ");
        if (!targetDescParts[0].endsWith("s")) {
            // targetDescription += "s";
            targetDescription = targetDescParts[0] + "s " + targetDescParts.slice(1).join(" ");
        }
        // Unless description contains " to ", add " to include" to clarify the purpose of the selection
        if (!targetDescription.includes(" to ")) {
            targetDescription += " to include";
        }
        mainText = `Use this widget to choose which <b>${targetDescription}</b>. You can select <b>visible</b> (default), <b>selected</b>, or <b>all</b> molecules. The summary text below updates to indicate exactly what will be processed. For this tour, simply press the "Next" button below to continue.`;
    }
    const label = arg.label || "Parameter";
    if (mainText === "") {
        mainText = `This is the <b>${label}</b> field. For this tour, leave it at its current value and press "Next" to continue.`;
    }
    mainText += buildSelectOptionsHtml(arg);
    const description = arg.description
        ? `<div class="mt-3 mb-0"><strong>Brief description:</strong> <em>${arg.description}</em></div>`
        : "";
    return {
        element: selector,
        popover: {
            title: plugin.title,
            description: `${mainText}${description}`,
        },
        isNoteStep: true,
        onHighlighted: (element) => {
            setFocus(element);
            // Temporarily disable the input to prevent changes that could
            // break the tour. The element is re-enabled when the step is
            // deselected (the user advances).
            if (element) {
                const inputEl = element;
                if (typeof inputEl.disabled !== "undefined") {
                    inputEl.disabled = true;
                }
            }
        },
        onDeselected: (element) => {
            // Re-enable the input when leaving this step so the form
            // remains functional after the tour.
            if (element) {
                const inputEl = element;
                if (typeof inputEl.disabled !== "undefined") {
                    inputEl.disabled = false;
                }
            }
        },
    };
}
